import React, { useEffect, useRef, useState } from 'react';
import { BackBar, BackButton } from '../../../layout/back-button';
import { FieldHint } from '../dashboard-buildings-components/dashboard-buildings.styled';
import { Eye } from '../../../icons/eye';
import { colors } from '../../../../style-utils/colors';
import { Text } from '../../../../style-utils/text';
import {
  AddNewUserForm,
  AddNewUserFormContainer,
  SaveButton,
  ButtonTitle,
  DashboardContent,
  InputContainer,
  Label,
  Title,
  ButtonsContainer,
  CancelButton,
  CheckboxesDropdown,
  ErrorLabel,
  FieldError,
  FormFieldset,
  CheckRingContainer,
  StyledSelectMenu,
  StyledUserInput,
  PasswordVisibilityButton,
  PropertySelectWrapper,
  PropertyCheckboxLabel,
  StyledSelectCheckboxes,
  CheckboxesContainerInSelectMenu,
} from './dashboard-users.styled';
import { Staleable, StatusLabel } from '../../../../motion';
import { useNavigate } from 'react-router-dom';
import {
  User,
  UserWithBuildingsObjects,
} from '../../../../interfaces/user-interfaces';
import { regexValidation } from '../../../../function-utils/regex-validation';
import { CheckRing } from '../../../icons/check-ring';
import { BuildingData } from '../../../../interfaces/building-interfaces';
import { fetchAllItems } from '../../../requests/requests';
import { RightArrow } from '../../../icons/right-arrow';
import axios from 'axios';
import { apiRoute } from '../../../../constants/api-constants';
import {
  ROLES,
  Role,
  ROLES_SET_HERE,
  normalizeRole,
  outranks,
  roleLabel,
} from '../../../../auth/roles';
import toast from '../../../../utils/toast';
import { useAuth } from '../../../../context/auth-provider';

const UserInitialState: UserWithBuildingsObjects = {
  id: '',
  full_name: '',
  email: '',
  password: '',
  property_ids: [],
  propertiesObjects: [],
  phone_number: '',
  role: '',
};

interface DashboardEditUserByIdProps {
  editableUserData: UserWithBuildingsObjects | User | undefined;
}

export const DashboardEditUserById: React.FC<DashboardEditUserByIdProps> = ({
  editableUserData,
}) => {
  // Editing your own account. The role control is read-only here — see the
  // comment next to it, and the matching 409 in users.js.
  const { user: signedInUser } = useAuth();
  const isSelf =
    !!signedInUser?.id &&
    String(signedInUser.id) === String(editableUserData?.id);
  // Independent Stays IS5: a co-host's role and reach are their listings,
  // changed on Stays → Team. Here only their name, e-mail, phone and password
  // are edited: the role is shown read-only and no building list is sent (the
  // server refuses one, users.js COHOST_BY_INVITATION).
  const isCohostTarget = normalizeRole(editableUserData?.role) === ROLES.STAYS_COHOST;
  // What this actor may set the role TO. Equal-or-below their own rank, which
  // is the same ceiling users.js applies — an Owner keeps all three (naming a
  // co-owner is theirs to do), a Property Manager gets Property Manager and
  // Concierge, and nobody is offered a role they cannot grant.
  // IS5: never co-host (made by invitation only, on Stays → Team).
  const assignableRoles: Role[] = ROLES_SET_HERE.filter(
    target =>
      normalizeRole(signedInUser?.role) === target ||
      outranks(signedInUser?.role, target)
  );
  // COVERAGE THAT CAME FROM A GRANT IS NOT A CHECKBOX LIST (WP-03).
  //
  // Since WP-02 the Property column shows COVERAGE — the union of explicit
  // building links and what an organization or portfolio grant expands to. The
  // checkboxes below write explicit links. So for somebody covered by a
  // portfolio grant, ticking these and saving would freeze today's buildings
  // into a fixed list and the portfolio would stop covering anything added to
  // it tomorrow. The first save would quietly undo a deliberate staffing
  // decision, and nothing on screen would say so.
  //
  // Read-only rather than hidden: the reader still needs to see which buildings
  // this person reaches. What they must not be offered is the illusion of
  // editing it here.
  const coverageIsDerived = Boolean(
    (editableUserData as UserWithBuildingsObjects | undefined)
      ?.coverage_derived
  );

  const [updatedUserData, setUpdatedUserData] = useState<
    UserWithBuildingsObjects | User
  >(
    editableUserData && editableUserData.email.length > 0
      ? editableUserData
      : UserInitialState
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const submitRef = useRef(false);
  const [invalidFieldsInputArray, setInvalidFieldsInputArray] = useState<
    string[]
  >([]);
  // Counts refused submits, so a field error that is already showing shakes
  // again on the next attempt instead of sitting still.
  const [attempt, setAttempt] = useState(0);
  const [properties, setProperties] = useState<BuildingData[]>([]);
  /* THIS FORM SETS A PASSWORD FOR SOMEBODY ELSE, and that person is not there
     to try it. The Add form learned this and grew a confirmation field; this one
     kept a single box, so a typo here locks a colleague out of the product and
     nobody finds out until they call.
     It also used to `.trim()` the value on every keystroke — the same defect the
     sign-in form had, which made any password with a leading or trailing space
     impossible to set. Removed: what is typed is what is sent. */
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isPropertiesCheckboxesOpened, setIsPropertiesCheckboxesOpened] =
    useState<boolean>(false);
  const checkboxesContainerRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();

  const getPropertiesfromDB = async () => {
    const response = await fetchAllItems('dashboard/buildings');

    setProperties(response as BuildingData[]);
  };

  useEffect(() => {
    getPropertiesfromDB();
  }, []);

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const validateNewUserData = (data: any) => {
    // PHONE IS NOT HERE ON PURPOSE. The server requires only email and
    // password (users.js), so requiring a phone here refused a save the
    // backend would have accepted — and a record saved without one could not
    // be edited at all until a number was invented for it.
    const managerFields: string[] = [
      'full_name',
      'email',
      // A co-host has listings, not buildings (IS5).
      ...(isCohostTarget ? [] : ['property_ids']),
      'role',
      'password',
    ];

    const emptyFields: string[] = [];

    managerFields.forEach(field => {
      if (data[field].length === 0 && field !== 'password') {
        emptyFields.push(field);
      }

      if (
        field === 'password' &&
        data.password.length < 8 &&
        data.password.length > 0
      ) {
        emptyFields.push('password');
      }
    });

    const emailValidation = regexValidation('email', updatedUserData.email);

    if (!emailValidation.isValidEmail) {
      emptyFields.push('email');
    }

    // Typed a new password? Then the two boxes must agree.
    if (data.password.length > 0 && data.password !== passwordConfirm) {
      emptyFields.push('passwordConfirm');
    }

    return emptyFields;
  };

  const handleInputChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = event.target;

    if (name === 'role') {
      setUpdatedUserData({ ...updatedUserData, [name]: value });
    } else if (name === 'phone_number') {
      // Validate phone number input to allow only numeric characters. An empty
      // box stays EMPTY: phone is optional, and a bare '+' would both fabricate
      // a value nobody typed and make clearing a saved number impossible.
      const numericValue = value.replace(/\D/g, '');

      setUpdatedUserData({
        ...updatedUserData,
        [name]: numericValue ? '+' + numericValue : '',
      });
    } else {
      // Handle all other inputs
      setUpdatedUserData({ ...updatedUserData, [name]: value });
    }
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();

    if (submitRef.current) {
      return;
    }

    const invalidFields = validateNewUserData(updatedUserData);

    setInvalidFieldsInputArray(invalidFields);

    if (invalidFields.length > 0) {
      setAttempt(count => count + 1);

      return;
    }

    submitRef.current = true;
    setIsLoading(true);
    try {
      const formData = new FormData();

      formData.append('id', updatedUserData.id);
      formData.append('full_name', updatedUserData.full_name);
      formData.append('email', updatedUserData.email);
      formData.append('password', updatedUserData.password);
      if (!isCohostTarget) {
        formData.append(
          'property_ids',
          JSON.stringify(updatedUserData.property_ids)
        );
      }
      // `|| ''` because a user saved without a phone comes back as null, and
      // FormData would append the literal string "null" for it.
      formData.append('phone_number', updatedUserData.phone_number || '');
      // role is nullable since migration 037 (a resident holds none), but this
      // is the STAFF edit form and the endpoint rejects anything outside
      // owner/property_manager/concierge with a 400. Sending the empty string
      // rather than the literal "null" keeps that rejection legible; the form's
      // own select cannot produce it, so it is a belt-and-braces cast.
      formData.append('role', updatedUserData.role || '');

      await axios.put(`${apiRoute}/dashboard/users/edit-user`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('User updated');
      setInvalidFieldsInputArray([]);
      navigate('/dashboard/users');
    } catch (error) {
      const message = axios.isAxiosError<{ message?: string }>(error)
        ? error.response?.data?.message
        : undefined;

      toast.error(message || 'Could not update the user. Please try again.');
    } finally {
      submitRef.current = false;
      setIsLoading(false);
    }
  };

  const showCheckboxes = () => {
    setIsPropertiesCheckboxesOpened(!isPropertiesCheckboxesOpened);
  };

  const clearData = () => {
    setUpdatedUserData(UserInitialState);
    setInvalidFieldsInputArray([]);
    navigate('/dashboard/users');
  };

  useEffect(() => {
    if (updatedUserData.email === '' || !updatedUserData.email) {
      navigate('/dashboard/users');
    }
  }, [editableUserData]);

  useEffect(() => {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    function handleClickOutside(event: any) {
      if (
        checkboxesContainerRef.current &&
        !checkboxesContainerRef.current.contains(event.target)
      ) {
        // Clicked outside the container, so close it
        setIsPropertiesCheckboxesOpened(false);
      }
    }

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleCheckboxChange = (propertyId: string, isChecked: boolean) => {
    if (normalizeRole(updatedUserData.role) === ROLES.CONCIERGE) {
      if (isChecked) {
        setUpdatedUserData(updatedUserData => ({
          ...updatedUserData,
          property_ids: [Number(propertyId)],
        }));
      } else {
        setUpdatedUserData(updatedUserData => ({
          ...updatedUserData,
          property_ids: [
            ...updatedUserData.property_ids.filter(
              item => item !== Number(propertyId)
            ),
          ],
        }));
      }
    } else {
      if (isChecked) {
        setUpdatedUserData(updatedUserData => ({
          ...updatedUserData,
          property_ids: [...updatedUserData.property_ids, Number(propertyId)],
        }));
      } else {
        setUpdatedUserData(updatedUserData => ({
          ...updatedUserData,
          property_ids: [
            ...updatedUserData.property_ids.filter(
              item => item !== Number(propertyId)
            ),
          ],
        }));
      }
    }
  };


  /* The buildings actually ticked, resolved against the list this form shows.
     Derived rather than stored: a second copy of the selection is how the label
     and the checkboxes disagreed in the first place. */
  const selectedPropertyNames = properties
    .filter(property =>
      (updatedUserData.property_ids || []).map(Number).includes(Number(property.id))
    )
    .map(property => property.property_name || 'Unnamed building');

  return (
    <DashboardContent id="dashboard-edit-user-form" isAddNewUser={true}>
      {/* A ROUTED PAGE NEEDS A WAY OUT (WP-10 / R-9). This is a route,
          not a modal — there is no scrim to click and no close button —
          so without this the only exit was the browser's own back.
          Modals deliberately do NOT get one: a BackBar beside a close
          affordance is the second dismiss control R-9 forbids. */}
      <BackBar>
        <BackButton to="/dashboard/users" label="Back to people" />
      </BackBar>
      <Title>
        <Text
          mt={16}
          ml={16}
          mb={0}
          fontSize={24}
          fontWeight={700}
          color={colors.grey_dark}
        >
          Edit user
        </Text>
        <Text mt={8} ml={16} mb={0} fontSize={14} color={`${colors.grey}`}>
          Please update the user
        </Text>
      </Title>

      <AddNewUserFormContainer>
        {/* SAVING KEEPS THE FORM ON SCREEN. The absolute spinner that covered
            these fields is gone: in flight, the form dims (Staleable), is inert
            (the fieldset is disabled, aria-busy says so) and the Save button
            carries the pending state at a stable width. */}
        <AddNewUserForm
          id="edit-user-form"
          aria-busy={isLoading || undefined}
          onSubmit={onSubmit}
        >
          <Staleable stale={isLoading}>
          <FormFieldset disabled={isLoading}>
          {/* Your own role is not editable here.
              Roles are read from the database on every request, so a change
              applies to your very next click — and stepping down from Owner
              costs you `user:edit`, which is the only permission that could put
              it back. The server refuses this too (409 SELF_ROLE_CHANGE); this
              just stops the control being offered in the first place. */}
          {isSelf || isCohostTarget ? (
            <InputContainer isRoleField={true}>
              <CheckRingContainer>
                <CheckRing />
              </CheckRingContainer>
              <StyledSelectMenu
                aria-label="Role"
                name="role"
                value={normalizeRole(updatedUserData.role) ?? ''}
                disabled
                isRoleField={true}
              >
                <option value={normalizeRole(updatedUserData.role) ?? ''}>
                  {roleLabel(updatedUserData.role)}
                </option>
              </StyledSelectMenu>
            </InputContainer>
          ) : (
            <InputContainer isRoleField={true}>
              <CheckRingContainer>
                <CheckRing />
              </CheckRingContainer>
              <StyledSelectMenu
                aria-label="Role"
                name="role"
                value={normalizeRole(updatedUserData.role) ?? ''}
                onChange={handleInputChange}
                isRoleField={true}
              >
                {/* ── THE CEILING, AS OPTIONS ────────────────────────────
                    The three roles used to be hardcoded, which was right while
                    only an Owner could reach this form. A property manager can
                    now edit a concierge, and offering them "Owner / Admin" in
                    the list would be offering the one choice the server always
                    refuses (users.js re-checks this on every request).
                    Equal-or-below, so a manager can still perform the promotion
                    the owner asked for: concierge -> Property Manager. */}
                {assignableRoles.map(assignable => (
                  <option key={assignable} value={assignable}>
                    {roleLabel(assignable)}
                  </option>
                ))}
              </StyledSelectMenu>
            </InputContainer>
          )}
          {isSelf && (
            <ErrorLabel as="p" role="note">
              You can’t change your own role — ask another Owner to do it.
            </ErrorLabel>
          )}
          {!isSelf && isCohostTarget && (
            <FieldHint role="note">
              A co-host works on the listings shared with them. Change those on
              Stays → Team.
            </FieldHint>
          )}

          {/* No avatar upload. `users.user_image` is written only by
              PATCH /profile — by the person themselves — and this route ignores
              an attached file, so the control silently discarded it. */}

          <>
            <InputContainer>
              <Label htmlFor="edit-user-name">User name</Label>
              <StyledUserInput
                id="edit-user-name"
                type="text"
                name="full_name"
                value={updatedUserData.full_name}
                placeholder={'Enter first and last name'}
                onChange={handleInputChange}
              />
            </InputContainer>
            <FieldError
              show={invalidFieldsInputArray.includes('full_name')}
              attempt={attempt}
            >
              Please enter first and last name*
            </FieldError>
          </>

          <InputContainer>
            <Label htmlFor="edit-user-email">Email</Label>
            <StyledUserInput
              id="edit-user-email"
              type="email"
              name="email"
              value={updatedUserData.email}
              placeholder={'Enter email address'}
              onChange={handleInputChange}
            />
          </InputContainer>
          <FieldError
            show={invalidFieldsInputArray.includes('email')}
            attempt={attempt}
          >
            Please enter a valid email address*
          </FieldError>

          <InputContainer isPasswordInput={true}>
            <Label htmlFor="edit-user-password">Password</Label>
            <StyledUserInput
              id="edit-user-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={updatedUserData.password}
              placeholder={'Enter password'}
              onChange={handleInputChange}
              aria-describedby="edit-user-password-hint"
              hasTrailingAction
            />
            <PasswordVisibilityButton
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Eye slashed={!showPassword} />
            </PasswordVisibilityButton>
          </InputContainer>
          {/* SAY WHAT BLANK MEANS. QA-08, 2026-08-27.
              The validator already treats an empty box as "leave the password
              alone" (see the `field !== 'password'` carve-out in the required
              check above), so the BEHAVIOUR was right and only the explanation
              was missing — and an administrator who cannot tell whether saving
              will wipe someone's credentials reasonably declines to save at all.
              Wired to the input with aria-describedby so it is announced, not
              just printed. */}
          <FieldHint id="edit-user-password-hint">
            Leave blank to keep the current password.
          </FieldHint>
          <FieldError
            show={invalidFieldsInputArray.includes('password')}
            attempt={attempt}
          >
            Your password must be at least 8 characters*
          </FieldError>

          {/* Only asked for once a new password is being set: an empty box on a
              form that is usually opened to fix a phone number is noise. */}
          {updatedUserData.password.length > 0 && (
            <>
              <InputContainer isPasswordInput={true}>
                <Label htmlFor="edit-user-password-confirm">
                  Confirm password
                </Label>
                <StyledUserInput
                  id="edit-user-password-confirm"
                  type={showPassword ? 'text' : 'password'}
                  name="passwordConfirm"
                  value={passwordConfirm}
                  placeholder={'Re-enter the password'}
                  onChange={e => setPasswordConfirm(e.target.value)}
                  hasTrailingAction
                />
              </InputContainer>
              <FieldError
                show={invalidFieldsInputArray.includes('passwordConfirm')}
                attempt={attempt}
              >
                The two passwords do not match*
              </FieldError>
            </>
          )}

          {/* ONE FIELD, NO DIAL-CODE SELECT. The select was hardcoded to +1
              and never seeded from the saved number, so it read "USA" beside a
              +44 one — and changing it BLANKED the number before applying the
              prefix, which this form then submitted. A stray click or a
              mouse-wheel over it destroyed a saved number. The dial code is
              part of the number the person types, as on account-profile.tsx. */}
          <InputContainer>
            <Label htmlFor="edit-user-phone">Phone</Label>
            <StyledUserInput
              id="edit-user-phone"
              type="tel"
              name="phone_number"
              autoComplete="tel"
              value={updatedUserData.phone_number || ''}
              placeholder={'Enter phone number (optional)'}
              onChange={handleInputChange}
            />
          </InputContainer>

          {!isCohostTarget && (
          <>
          <InputContainer>
            <Label htmlFor="edit-user-properties">Property selection</Label>

            <PropertySelectWrapper ref={checkboxesContainerRef}>
              <StyledSelectCheckboxes
                id="edit-user-properties"
                type="button"
                aria-haspopup="true"
                aria-expanded={isPropertiesCheckboxesOpened}
                aria-controls="property-checkboxes"
                onClick={showCheckboxes}
              >
                {/* READ FROM THE SAME FIELD THE CHECKBOXES WRITE.
                    This label used to read `propertiesObjects[0]`, while the
                    checkboxes below read and write `property_ids` and never
                    touch propertiesObjects. So the summary showed whatever the
                    user was assigned when the form loaded, no matter what was
                    ticked afterwards — and it named only the FIRST of them, so a
                    manager on four buildings saw one. Two controls, one fact,
                    two different answers.
                    It also names them rather than pasting an address after a
                    comma, which is what "Aurora Tower,12 Bay St" was. */}
                {selectedPropertyNames.length === 0
                  ? 'Property selection'
                  : selectedPropertyNames.length <= 2
                    ? selectedPropertyNames.join(' and ')
                    : `${selectedPropertyNames.length} buildings selected`}
              </StyledSelectCheckboxes>
              {/* Unfolds under the button (Collapse) and folds away on
                  dismissal; aria-controls still points at the id. */}
              <CheckboxesDropdown
                open={isPropertiesCheckboxesOpened}
                id="property-checkboxes"
              >
                <CheckboxesContainerInSelectMenu
                  role="group"
                  aria-label="Properties"
                >
                  {properties.map(property => (
                    <PropertyCheckboxLabel key={property.id}>
                      <input
                        name="property_ids"
                        type="checkbox"
                        value={property.id}
                        disabled={coverageIsDerived}
                        checked={updatedUserData.property_ids.includes(
                          Number(property.id) as never
                        )}
                        onChange={e =>
                          handleCheckboxChange(property.id, e.target.checked)
                        }
                      />
                      {property.property_name}, {property.address}
                    </PropertyCheckboxLabel>
                  ))}
                </CheckboxesContainerInSelectMenu>
              </CheckboxesDropdown>
            </PropertySelectWrapper>
          </InputContainer>
          {coverageIsDerived && (
            <FieldHint>
              These buildings come from an organisation- or portfolio-wide
              grant, so they are not edited here. Change the grant on the
              staff access screen and this list follows it — including any
              building added to that portfolio later.
            </FieldHint>
          )}
          <FieldError
            show={
              !coverageIsDerived &&
              invalidFieldsInputArray.includes('property_ids')
            }
            attempt={attempt}
          >
            Please select at least one property*
          </FieldError>
          </>
          )}
          </FormFieldset>
          </Staleable>
        </AddNewUserForm>
      </AddNewUserFormContainer>

      <ButtonsContainer>
        <CancelButton
          type="button"
          disabled={isLoading}
          onClick={() => clearData()}
        >
          <ButtonTitle>Cancel</ButtonTitle>
        </CancelButton>
        <SaveButton type="submit" form="edit-user-form" disabled={isLoading}>
          <StatusLabel
            state={isLoading ? 'busy' : 'idle'}
            labels={{
              idle: (
                <>
                  <ButtonTitle>Save</ButtonTitle>
                  <RightArrow />
                </>
              ),
              busy: <ButtonTitle>Saving…</ButtonTitle>,
            }}
          />
        </SaveButton>
      </ButtonsContainer>
    </DashboardContent>
  );
};
